import * as Yup from "yup";

import { FieldProps } from "types/form";

type Section = Record<string, FieldProps>;

export type FieldsConfig = Record<string, Section>;

function buildYupSchemaTree(tree: any): Yup.AnyObjectSchema {
  const shape: Record<string, any> = {};
  for (const key in tree) {
    if (Object.prototype.hasOwnProperty.call(tree, key)) {
      const value = tree[key];
      if (Yup.isSchema(value)) {
        shape[key] = value;
      } else if (typeof value === "object") {
        shape[key] = buildYupSchemaTree(value);
      }
    }
  }
  return Yup.object().shape(shape);
}

export function booleanOrArrayValidator(
  label: string,
  isRequired = false
): Yup.MixedSchema {
  return Yup.mixed().test({
    name: "boolean-or-array",
    test(value, ctx) {
      const isBoolean = typeof value === "boolean";
      const isArray = Array.isArray(value);

      if (!isBoolean && !isArray) {
        return ctx.createError({
          message: `${label} must be either a boolean or an array.`,
        });
      }

      if (isRequired) {
        if (isBoolean && value === false) {
          return ctx.createError({
            message: `${label} is required.`,
          });
        }
        if (isArray && value.length === 0) {
          return ctx.createError({
            message: `${label} requires at least one element.`,
          });
        }
      }

      return true;
    },
  });
}

export function applyDateValidations(
  inputSchema: Yup.DateSchema,
  field: FieldProps
) {
  let schema = inputSchema;
  if (field.min instanceof Date) {
    schema = schema.min(field.min);
  }
  if (field.max instanceof Date) {
    schema = schema.max(field.max);
  }
  return schema;
}

export function applyJsonValidations(
  inputSchema: Yup.StringSchema,
  field: FieldProps
): Yup.StringSchema {
  return inputSchema.test({
    name: "is-json-valid",
    test(value, ctx) {
      if (!value) {
        return true;
      }
      try {
        const data = JSON.parse(value);
        if (field.validateData) {
          const result = field.validateData(data);
          if (typeof result === "string" || result === false) {
            return ctx.createError({
              message:
                `${field.label} has an invalid format.` +
                (typeof result === "string" ? ` ${result}` : ""),
            });
          }
        }
      } catch {
        return ctx.createError({
          message: `${field.label} is not a valid JSON.`,
        });
      }
      return true;
    },
  });
}

export function applyNumberValidations(
  inputSchema: Yup.NumberSchema,
  field: FieldProps
): Yup.NumberSchema {
  let schema = inputSchema;
  if (typeof field.min === "number") {
    schema = schema.min(
      field.min,
      `${field.label} must be at least ${field.min}.`
    );
  }
  if (typeof field.max === "number") {
    schema = schema.max(
      field.max,
      `${field.label} must be at most ${field.max}.`
    );
  }
  return schema;
}

export function applyStringValidations(
  inputSchema: Yup.StringSchema,
  field: FieldProps
): Yup.StringSchema {
  let schema = inputSchema;
  if (field.minLength !== undefined) {
    schema = schema.min(
      field.minLength,
      `${field.label} must be at least ${field.minLength} characters long.`
    );
  }
  if (field.maxLength !== undefined) {
    schema = schema.max(
      field.maxLength,
      `${field.label} must be at most ${field.maxLength} characters long.`
    );
  }
  if (field.pattern) {
    try {
      const regex = new RegExp(field.pattern);
      schema = schema.matches(
        regex,
        `${field.label} has an invalid format` +
          (field.patternHint ? ` (format: ${field.patternHint}).` : ".")
      );
    } catch {
      console.warn(
        `Invalid RegExp pattern in the field "${field.name}":`,
        field.pattern
      );
    }
  }
  return schema;
}

export const createValidationSchema = (
  fieldsConfig: FieldsConfig
): Yup.AnyObjectSchema => {
  const schemaTree: Record<string, any> = {};

  Object.values(fieldsConfig).forEach((section) => {
    Object.values(section).forEach((field) => {
      const path = field.name;
      const isRequired = field.required !== false; // default: required = true
      const type = field.type ?? "text"; // default to 'text' if type is not defined
      let validator: Yup.AnySchema;

      switch (type) {
        case "checkbox":
          validator = booleanOrArrayValidator(field.label, isRequired);
          break;
        case "switch":
          validator = Yup.boolean();
          break;
        case "date":
        case "datetime-local":
          validator = applyDateValidations(Yup.date(), field);
          break;
        case "email":
          validator = applyStringValidations(
            Yup.string().email(`${field.label} must be a valid email.`),
            field
          );
          break;
        case "json":
          validator = applyJsonValidations(
            applyStringValidations(Yup.string(), field),
            field
          );
          break;
        case "number":
          validator = applyNumberValidations(Yup.number(), field);
          break;
        case "radio":
        case "select":
          validator = Yup.string();
          break;
        case "text":
        case "textarea":
          validator = applyStringValidations(Yup.string(), field);
          break;
        case "url":
          validator = applyStringValidations(
            Yup.string().url(`${field.label} must be a valid URL.`),
            field
          );
          break;
        default:
          validator = Yup.mixed(); // fallback
          break;
      }
      // Apply required validation for all types except checkbox
      // (checkbox handles required validation in its own case)
      if (isRequired && type !== "checkbox") {
        validator = validator.required(`${field.label} is required.`);
      }

      // Insert validator into nested structure
      const segments = path.split(".");
      let current = schemaTree;

      segments.forEach((segment, index) => {
        if (index === segments.length - 1) {
          current[segment] = validator;
        } else {
          current[segment] = current[segment] || {};
          current = current[segment];
        }
      });
    });
  });

  return buildYupSchemaTree(schemaTree);
};
